import { MissingRequiredField } from '../../../utils/fieldGroupEdit';

/**
 * SKIN-543 — the pure part of closing / reopening a visit from the patient page;
 * SKIN-696 — and voiding a DRAFT one (DRAFT → VOIDED, one-way).
 */

export type VisitStatusChange = 'close' | 'reopen' | 'void';

/**
 * What a visit in `status` offers from the web, decided by one switch over the
 * status: a set of actions in display order (a DRAFT offers close AND void,
 * SKIN-696; a SUBMITTED one reopen), the `locked` hint ("void the QC approval
 * first"), or nothing. The `locked`-vs-nothing distinction — both of which have
 * no actions — is carried by the union tag, so the caller reads a single result.
 */
export type VisitStatusOffer =
    | { kind: 'actions'; actions: VisitStatusChange[] }
    | { kind: 'locked' }
    | null;

export function visitStatusOffer(status?: string): VisitStatusOffer {
    if (status === 'DRAFT') return { kind: 'actions', actions: ['close', 'void'] };
    if (status === 'SUBMITTED') return { kind: 'actions', actions: ['reopen'] };
    if (status === 'LOCKED') return { kind: 'locked' };
    return null;
}

/**
 * Below this age of the visit's last submission, closing warns that a phone may still hold
 * unsent data for the visit. The server has no signal of what a phone holds; 14 days is the
 * age at which SKIN-303 treats a DRAFT as abandoned. A warning, not a guarantee.
 */
export const RECENT_DEVICE_ACTIVITY_DAYS = 14;

export function hasRecentDeviceActivity(lastSubmissionAt: Date | string | null | undefined, now = Date.now()): boolean {
    if (!lastSubmissionAt) return false;
    const at = new Date(lastSubmissionAt).getTime();
    if (Number.isNaN(at)) return false;
    return now - at < RECENT_DEVICE_ACTIVITY_DAYS * 86_400_000;
}

/**
 * One of the visit author's OTHER open drafts on the same case, as
 * GET .../web/reopen-check lists them (oldest first). The reviewer usually cannot see
 * them in the visit list: on CLINICAL studies other researchers' drafts are hidden.
 */
export interface OtherOpenDraft {
    uuid: string;
    createdAt: Date | string;
    /** null when the draft has no data yet */
    lastSubmissionAt: Date | string | null;
}

/** Codes the API answers a refused web close/reopen/void with (visitStatusWeb.controller.ts). */
const KNOWN_ERRORS = ['REASON_REQUIRED', 'VISIT_NOT_DRAFT', 'VISIT_NOT_SUBMITTED', 'VISIT_LOCKED',
    'CASE_CLOSED', 'VISIT_EMPTY', 'STATUS_CHANGED', 'VISIT_VOIDED'];

/** As the API lists it: each missing field names the submission it is missing from. */
export type VisitMissingRequiredField = MissingRequiredField & { submissionId?: number };

export type VisitStatusRefusal =
    | { kind: 'missing'; missing: VisitMissingRequiredField[] }
    | { kind: 'message'; translationId: string }
    | { kind: 'raw'; message: string };

/** What to tell the reviewer about a rejected change: the field list, a known code, or the server's words. */
export function decodeVisitStatusRefusal(error: any): VisitStatusRefusal {
    const body = error?.response?.data ?? error;
    if (body?.error === 'MISSING_REQUIRED_FIELDS' && Array.isArray(body.missingRequiredFields)) {
        return { kind: 'missing', missing: body.missingRequiredFields };
    }
    if (typeof body?.error === 'string' && KNOWN_ERRORS.includes(body.error)) {
        return { kind: 'message', translationId: `hospital.visit-status-error-${body.error}` };
    }
    if (typeof body?.errorMessage === 'string' && body.errorMessage) {
        return { kind: 'raw', message: body.errorMessage };
    }
    return { kind: 'message', translationId: 'hospital.visit-status-error-generic' };
}
